import React, {
  forwardRef,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
  ForwardedRef,
  RefObject,
  TextareaHTMLAttributes,
  useImperativeHandle,
  useRef,
  HTMLAttributes,
  isValidElement,
} from 'react';
import clsx from 'clsx';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';
import { PressEvents } from '@react-types/shared';
import { createFocusableRef } from '@react-spectrum/utils';
import textFieldStyles from '@danske/sapphire-css/components/textField/textField.module.css';

import { TextFieldRef } from '..';
import { Field } from '../../Field';
import { Label } from '../../Label';
import { useAutofillStyle } from './useAutofillStyle';
import { SapphireTextFieldProps } from './useSapphireTextField';
import { SapphireStyleProps } from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';

export type TextFieldBaseProps<T extends 'input' | 'textarea'> =
  (T extends 'input'
    ? SapphireTextFieldProps<'input'>
    : SapphireTextFieldProps<'textarea'>) &
    PressEvents &
    SapphireStyleProps &
    GlobalDomAttributes & {
      prefix?: ReactNode;
      postfix?: ReactNode;
      alignInputRight?: boolean;
      inputElementType: T;
      containerClassName?: string;
      inputRef: RefObject<
        T extends 'input' ? HTMLInputElement : HTMLTextAreaElement
      >;
      labelProps: LabelHTMLAttributes<HTMLLabelElement>;
      inputProps:
        | InputHTMLAttributes<HTMLInputElement>
        | TextareaHTMLAttributes<HTMLTextAreaElement>;
      errorMessageProps: HTMLAttributes<HTMLElement>;
      descriptionProps: HTMLAttributes<HTMLElement>;
      size?: 'lg' | 'md';
      labelPlacement?: 'side' | 'above';
    };

const TextFieldBaseComponent = <T extends 'input' | 'textarea'>(
  props: TextFieldBaseProps<T>,
  ref: ForwardedRef<TextFieldRef>
) => {
  const {
    label,
    isDisabled,
    error,
    note,
    inputElementType,
    isRequired,
    labelProps,
    inputProps,
    errorMessageProps,
    descriptionProps,
    containerClassName,
    characterCounter,
    contextualHelp,
    inputRef,
    prefix,
    postfix,
    size = 'lg',
    labelPlacement = 'above',
    necessityIndicator = false,
    alignInputRight,
    ...otherProps
  } = props;

  const { focusProps, isFocusVisible } = useFocusRing();
  const { autofillStyles, updateStyle } = useAutofillStyle(inputRef);

  const domRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    ...createFocusableRef(domRef, inputRef),

    /**
     * (This function is deprecated. Use `inputRef` prop)
     * @deprecated
     */
    getInputElement() {
      return inputRef.current;
    },
  }));

  const ElementType: 'input' | 'textarea' = inputElementType;
  const isMedium = size === 'md';

  return (
    <Field
      // otherProps contains some of the same props as inputProps. That is
      // intended, but some DOM props, like "id", should not be repeated
      {...removeUniqueDOMProps(otherProps)}
      ref={domRef}
      size={size}
      labelPlacement={labelPlacement}
    >
      <Field.Context
        descriptionProps={error ? errorMessageProps : descriptionProps}
      >
        {label && (
          <Field.Label>
            <Label
              {...labelProps}
              size={size}
              necessityIndicator={
                isRequired && necessityIndicator
                  ? 'required'
                  : !isRequired && necessityIndicator
                  ? 'optional'
                  : undefined
              }
              contextualHelp={contextualHelp}
            >
              {label}
            </Label>
          </Field.Label>
        )}
        <Field.Control>
          <div
            className={clsx(
              textFieldStyles['sapphire-text-field'],
              containerClassName,
              {
                [textFieldStyles['is-focus']]: isFocusVisible,
                [textFieldStyles['sapphire-text-field--error']]: !!error,
                [textFieldStyles['sapphire-text-field--md']]: isMedium,
              }
            )}
          >
            {prefix && (
              <span
                className={clsx(
                  textFieldStyles['sapphire-text-field__prefix'],
                  {
                    [textFieldStyles['sapphire-text-field__prefix--icon']]:
                      isValidElement(prefix),
                  }
                )}
              >
                {prefix}
              </span>
            )}
            <ElementType
              {...mergeProps(inputProps, focusProps, {
                onChange: updateStyle,
                onBlur: updateStyle,
              })}
              /* @ts-expect-error the ref type matches the ElementType but TS does not know that */
              ref={inputRef}
              className={clsx(textFieldStyles['sapphire-text-field__input'], {
                [textFieldStyles['sapphire-text-field__input--align-right']]:
                  !!alignInputRight,
              })}
              style={autofillStyles}
            />
            {postfix && (
              <span
                className={clsx(
                  textFieldStyles['sapphire-text-field__postfix'],
                  {
                    [textFieldStyles['sapphire-text-field__postfix--icon']]:
                      isValidElement(postfix),
                  }
                )}
              >
                {postfix}
              </span>
            )}
          </div>
        </Field.Control>
        {/* errorMessageProps use aria-describedby for error messages because
          aria-errormessage is unsupported using VoiceOver or NVDA. See
          https://github.com/adobe/react-spectrum/issues/1346#issuecomment-740136268*/}
        {((!!error && typeof error !== 'boolean') ||
          note ||
          characterCounter) && (
          <Field.Footer>
            {!!error && typeof error !== 'boolean' ? (
              <Field.Note variant="error">{error}</Field.Note>
            ) : note ? (
              note
            ) : null}
            {characterCounter && (
              <span
                className={clsx(
                  textFieldStyles['sapphire-text-field__counter'],
                  {
                    [textFieldStyles['sapphire-text-field__counter--error']]:
                      characterCounter &&
                      characterCounter.current > characterCounter.max,
                  }
                )}
              >
                {Math.max(characterCounter.current, 0)}/
                {Math.max(characterCounter.max, 0)}
              </span>
            )}
          </Field.Footer>
        )}
      </Field.Context>
    </Field>
  );
};

const removeUniqueDOMProps = (props: Record<any, any>): Record<any, any> =>
  Object.fromEntries(
    Object.entries(props).filter(
      ([name]) => name !== 'id' && !name.startsWith('data-')
    )
  );

export const TextFieldBase = forwardRef(TextFieldBaseComponent);
